import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../services/firebase/config';

// Add bypass flag for users created before email verification was required
const BYPASS_VERIFICATION_FOR_EXISTING_USERS = true;

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const [isVerified, setIsVerified] = useState<boolean | null>(null);
  const [userCreationTime, setUserCreationTime] = useState<Date | null>(null);
  const [bypassVerification, setBypassVerification] = useState(false);

  useEffect(() => {
    if (currentUser) {
      // Set up listener for auth state changes to check email verification
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        if (user) {
          // Check user creation time to determine if they should bypass verification
          if (BYPASS_VERIFICATION_FOR_EXISTING_USERS && user.metadata && user.metadata.creationTime) {
            const creationTime = new Date(user.metadata.creationTime);
            setUserCreationTime(creationTime);
            
            // Get current date
            const currentDate = new Date();
            // Add verification bypass for users created more than 1 day ago (existing users)
            const oneDayInMs = 24 * 60 * 60 * 1000;
            if (currentDate.getTime() - creationTime.getTime() > oneDayInMs) {
              setBypassVerification(true);
              setIsVerified(true); // Treat as verified
              return;
            }
          }
          
          // For newer users, check email verification
          // Reload user to get fresh verification status
          user.reload().then(() => {
            setIsVerified(user.emailVerified);
          }).catch(() => {
            setIsVerified(false);
          });
        } else {
          setIsVerified(null);
        }
      });

      return () => unsubscribe();
    } else {
      setIsVerified(null);
    }
  }, [currentUser]);
  
  // If no user is logged in, redirect to login
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  
  // If still checking verification status
  if (isVerified === null) {
    return <div className="loading-indicator">Loading...</div>;
  }
  
  // If email is not verified and not bypassing verification, redirect to confirm email page
  if (!isVerified && !bypassVerification) {
    return <Navigate to="/confirm-email" />;
  }
  
  // If user is logged in and email is verified, render the children
  return <>{children}</>;
};

export default ProtectedRoute;
